// Context Providers for Gift-Wish
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { translations, dateLocales, availableCurrencies, getBrowserLanguage } from "@/i18n";

// Country to language/currency mapping
const countrySettings = {
  // Russia & CIS
  RU: { language: "ru", currency: "₽" },
  BY: { language: "ru", currency: "₽" },
  KZ: { language: "ru", currency: "₽" },
  // Ukraine
  UA: { language: "uk", currency: "₴" },
  // Czech Republic
  CZ: { language: "cs", currency: "Kč" },
  // Slovakia
  SK: { language: "sk", currency: "€" },
  // Germany, Austria
  DE: { language: "de", currency: "€" },
  AT: { language: "de", currency: "€" },
  CH: { language: "de", currency: "CHF" },
  // France, Belgium, Canada
  FR: { language: "fr", currency: "€" },
  BE: { language: "fr", currency: "€" },
  CA: { language: "en", currency: "$" },
  // Spain, Latin America
  ES: { language: "es", currency: "€" },
  MX: { language: "es", currency: "$" },
  AR: { language: "es", currency: "$" },
  // USA & UK
  US: { language: "en", currency: "$" },
  GB: { language: "en", currency: "£" },
  // Eurozone defaults
  IT: { language: "en", currency: "€" },
  NL: { language: "en", currency: "€" },
  PL: { language: "en", currency: "zł" },
  PT: { language: "en", currency: "€" },
};

// Get currency based on user's timezone/region (fallback)
const getRegionCurrency = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    
    // Map regions to currencies
    const regionCurrencyMap = {
      // Russia
      'Europe/Moscow': '₽', 'Europe/Kaliningrad': '₽', 'Europe/Samara': '₽',
      'Asia/Yekaterinburg': '₽', 'Asia/Novosibirsk': '₽', 'Asia/Vladivostok': '₽',
      // Ukraine
      'Europe/Kiev': '₴', 'Europe/Kyiv': '₴', 'Europe/Uzhgorod': '₴',
      'Europe/Zaporozhye': '₴', 'Europe/Simferopol': '₴',
      // USA
      'America/New_York': '$', 'America/Chicago': '$', 'America/Denver': '$',
      'America/Los_Angeles': '$', 'America/Phoenix': '$',
      // UK
      'Europe/London': '£',
      // Czech Republic
      'Europe/Prague': 'Kč',
      // Eurozone
      'Europe/Paris': '€', 'Europe/Berlin': '€', 'Europe/Madrid': '€',
      'Europe/Rome': '€', 'Europe/Amsterdam': '€', 'Europe/Vienna': '€',
      'Europe/Brussels': '€', 'Europe/Lisbon': '€', 'Europe/Dublin': '€',
      'Europe/Helsinki': '€', 'Europe/Athens': '€', 'Europe/Bratislava': '€',
    };
    
    // Check timezone first
    if (regionCurrencyMap[timezone]) {
      return regionCurrencyMap[timezone];
    }
    
    // Fallback to locale-based detection
    if (locale.startsWith('uk')) return '₴';
    if (locale.startsWith('ru')) return '₽';
    if (locale.startsWith('en-US')) return '$';
    if (locale.startsWith('en-GB')) return '£';
    if (locale.startsWith('cs')) return 'Kč';
    
    // Default to Euro for unknown regions
    return '€';
  } catch {
    return '€';
  }
};

// ============== Language Context ==============
const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem("language");
    return saved || getBrowserLanguage();
  });
  
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem("currency");
    if (saved) return saved;
    return getRegionCurrency();
  });
  
  const [geoDetected, setGeoDetected] = useState(false);

  const t = (key) => translations[language]?.[key] || translations.en[key] || key;
  const dateLocale = dateLocales[language] || "en-US";

  const changeLanguage = (newLang) => {
    setLanguage(newLang);
    localStorage.setItem("language", newLang);
    document.documentElement.lang = newLang;
    // Sync language to backend for email notifications
    const token = localStorage.getItem("session_token");
    if (token) {
      const API = process.env.REACT_APP_BACKEND_URL;
      fetch(`${API}/api/user/language`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ language: newLang }),
      }).catch(() => {});
    }
  };

  // Auto-detect country and set language/currency on first visit
  useEffect(() => {
    const detectCountry = async () => {
      // Skip if already saved preferences or already detected
      if (localStorage.getItem("language") && localStorage.getItem("currency")) {
        return;
      }
      if (geoDetected) return;
      
      try {
        // Try to get country from IP (using free service)
        const response = await fetch("https://ipapi.co/json/", { 
          signal: AbortSignal.timeout(3000) 
        });
        
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code;
          
          if (countryCode && countrySettings[countryCode]) {
            const settings = countrySettings[countryCode];
            
            // Set language if not manually set
            if (!localStorage.getItem("language")) {
              changeLanguage(settings.language);
            }
            
            // Set currency if not manually set
            if (!localStorage.getItem("currency")) {
              setCurrency(settings.currency);
              localStorage.setItem("currency", settings.currency);
            }
          }
        }
      } catch (error) {
        // Silently fail - use defaults from browser
        console.debug("Geo detection failed, using browser defaults");
      } finally {
        setGeoDetected(true);
      }
    };
    
    detectCountry();
  }, [geoDetected]);

  useEffect(() => {
    localStorage.setItem("currency", currency);
  }, [currency]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t, currency, setCurrency, dateLocale, availableCurrencies, getRegionCurrency }}>
      {children}
    </LanguageContext.Provider>
  );
};

// ============== Auth Context ==============
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshSession = useCallback(async () => {
    // Prevent multiple simultaneous refresh attempts
    if (isRefreshing) return false;
    
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;
    
    setIsRefreshing(true);
    try {
      const response = await api.post(`/auth/refresh`, {
        refresh_token: refreshToken
      });
      
      setUser(response.data.user);
      
      // Update localStorage tokens
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      return true;
    } catch {
      // Clear tokens on refresh failure
      localStorage.removeItem('session_token');
      localStorage.removeItem('refresh_token');
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing]);

  const checkAuth = useCallback(async () => {
    const sessionToken = localStorage.getItem('session_token');
    if (!sessionToken) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get(`/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Try to refresh session if auth failed (only once)
      if (error.response?.status === 401 && !isRefreshing) {
        const refreshed = await refreshSession();
        if (!refreshed) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSession]);

  const logout = async () => {
    try {
      await api.post(`/auth/logout`, {});
    } catch (e) {
      console.error("Logout error:", e);
    }
    setUser(null);
    localStorage.removeItem('session_token');
    localStorage.removeItem('refresh_token');
  };

  // Auto-refresh session periodically (every 10 minutes for better UX)
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      refreshSession();
    }, 10 * 60 * 1000); // 10 minutes
    
    return () => clearInterval(interval);
  }, [user, refreshSession]);

  // Check auth on initial load
  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
};
